/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Avatar from "@mui/material/Avatar";
import { blue } from "@mui/material/colors";

// Material Dashboard 2 PRO React components
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDButton from "components/MDButton";

// Service
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  code: "",
  customer_id: "",
  saleman_id: "",
  branch_id: "",
  name: "",
  project_type: "BUY",
  remark: "",
  total: "",
  vat: "",
  nettotal: "",
  commission: "",
  status: "",
  created_at: "",
  updated_at: "",
  date_record: Func.dateNow(),
};

const cboProjectType = [
  { id: "SUB", name: "ค่าบริการรายปี" },
  { id: "BUY", name: "ซื้อขาด" },
];

function TaskAdd({ parent, callbackOpen, callbackNotify }) {
  const mod = "task";
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const init = () => {
    setAttr(initAttr);
    setOpen(true);
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.project_type, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setConfirm(false);
    callbackOpen(false);
    setOpen(false);

    FetchData("PUT", `/${mod}`, app.token, {
      customer_id: parent.id,
      project_type: attr.project_type,
      total: attr.total,
      vat: attr.vat,
      nettotal: attr.nettotal,
      commission: attr.commission,
      remark: attr.remark,
    }).then((result) => {
      if (result.status === "ok") {
        callbackNotify({ open: true, title: t(`alert.addcomplete`), color: "success" });
      } else {
        callbackNotify({ open: true, title: t(`alert.saveerror`), color: "error" });
      }
    });
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label="ลูกค้า"
            value={parent.name}
            variant="standard"
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label="ประเภท"
            labelDefault={t(`element.please_select`)}
            data={cboProjectType}
            value={attr.project_type}
            onChange={(e) => setAttr({ ...attr, project_type: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.remark`)}
            onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
            value={attr.remark}
            variant="standard"
            multiline
            inputProps={{
              maxLength: 200,
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderDialog = () => {
    return (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setOpen(false)}
              aria-label="close"
            >
              <Icon>arrow_back_ios</Icon>
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              สร้างการติดตาม
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent dividers>{renderInfo()}</DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderButton = () => {
    return (
      <ListItem disableGutters>
        <ListItemButton autoFocus onClick={init}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: blue[100], color: blue[600] }}>
              <Icon>add_task</Icon>
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="สร้างการติดตาม" />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <div>
      {renderButton()}
      {renderDialog()}
    </div>
  );
}

TaskAdd.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  callbackOpen: PropTypes.func.isRequired,
  callbackNotify: PropTypes.func.isRequired,
};

export default TaskAdd;
